import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";

const emojis = [
    "🐶", "🚗", "🦄", "🎈", "🚀", "🐘", "🍎", "🏀", "📕", "⚽",
    "🍕", "🎸", "🚢", "🐱", "🌞", "🍔", "🚲", "🏕", "📡", "🔑",
    "🐳", "🌵", "🎠", "🍩", "🧀", "🚂", "🐙", "🦆", "🏖", "📖",
    "🍉", "🌊", "🎻", "🌈", "🍟", "⚡", "🎯", "🐟", "🚜", "🦅",
    "📱", "📚", "🥕", "🦖", "🎃", "🐬", "🎁", "🍇", "🚀", "🛶",
    "🦜", "🏹", "🐧", "🦊", "🏰", "🚦", "🎠", "🌼", "💎", "🗻",
    "🦄", "📌", "🥦", "🎷", "🔬", "🐼", "📎", "🦕", "🍵", "⚔️",
    "🦉", "🌙", "🎭", "🏜", "📀", "🦢", "🐎", "🧊", "🎇", "🏄",
    "🚁", "🦋", "🦔", "🍪", "🛰", "🐝", "🎤", "🦑", "🏎", "🍍"
];

const BigOrSmallGame: React.FC = () => {
    const [emoji, setEmoji] = useState<string>("🍎");
    const [questionType, setQuestionType] = useState<string>("big"); // "big" or "small"
    const [bigOnLeft, setBigOnLeft] = useState<boolean>(true);
    const [correctAnswer, setCorrectAnswer] = useState<string>("");
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);

    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "Big and Small – LKG Pre-Math Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const generateNewQuestion = () => {
        // Choose a random emoji
        const selectedEmoji = emojis[Math.floor(Math.random() * emojis.length)];
        setEmoji(selectedEmoji);

        // Randomly decide if the left or right emoji is bigger
        const isLeftBig = Math.random() > 0.5;
        setBigOnLeft(isLeftBig);

        // Randomly decide if the question asks for "big" or "small"
        const isBigQuestion = Math.random() > 0.5;
        setQuestionType(isBigQuestion ? "big" : "small");

        // Determine the correct answer
        const correct = isBigQuestion ? (isLeftBig ? "left" : "right") : (isLeftBig ? "right" : "left");
        setCorrectAnswer(correct);

        setMessage("");
    };

    const checkAnswer = (selected: string) => {
        if (selected === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Help your LKG child learn the concept of big and small with engaging activities on Mama’s Guidance—designed to develop early comparison skills through fun visuals and interactive exercises perfect for young learners.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container game-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Big and Small</h1>

                                    <p>
                                        Introduce your child to one of the first and most important <strong>pre-math concepts</strong> – <strong>Big and Small</strong>! This worksheet helps LKG students visually compare objects and understand size differences through simple, colorful exercises.
                                    </p>

                                    <h2>📏 What Are Pre-Math Concepts?</h2>
                                    <p>
                                        Pre-math concepts form the base for early math learning. Before counting or adding, children need to understand ideas like <b><em>size, quantity, and position</em></b>. Learning <strong>big and small</strong> teaches kids how to compare and classify objects around them.
                                    </p>

                                    <h2>🧩 What’s in the Worksheet?</h2>
                                    <ul>
                                        <li>Picture-based comparison of big and small objects</li>
                                        <li>Circle the <strong>bigger/smaller</strong> item</li>
                                        <li>Match big objects with small versions</li>
                                        <li>Coloring or tracing based on size</li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Recognize size differences: big vs. small</li>
                                        <li>Compare two or more objects visually</li>
                                        <li>Improve classification and early observation skills</li>
                                        <li>Strengthen vocabulary using descriptive size terms</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use the Worksheet</h2>
                                    <ul>
                                        <li>Show your child the pictures and ask: <strong>“Which is big? Which is small?”</strong></li>
                                        <li>Let them circle, color, or match the correct items.</li>
                                        <li>Reinforce learning with real-life examples: <strong>big ball vs. small ball, big spoon vs. small spoon</strong>.</li>
                                        <li>Encourage conversation using the words <strong>“big”</strong> and <strong>“small”</strong> in daily routines.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/long-short"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Long - Short</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/heavy-light"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Heavy - Light</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/full-empty"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Full - Empty</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why This Activity Is Important</h2>
                                    <p>
                                        Understanding size is one of the <strong>first comparison skills</strong> children learn. It prepares them for math concepts like measurement, ordering, and even counting. It also improves their ability to observe and describe the world around them.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Big and Small</h1>

                                <p>
                                    Introduce your child to one of the first and most important <strong>pre-math concepts</strong> – <strong>Big and Small</strong>! This worksheet helps LKG students visually compare objects and understand size differences through simple, colorful exercises.
                                </p>

                                <h2>📏 What Are Pre-Math Concepts?</h2>
                                <p>
                                    Pre-math concepts form the base for early math learning. Before counting or adding, children need to understand ideas like <b><em>size, quantity, and position</em></b>. Learning <strong>big and small</strong> teaches kids how to compare and classify objects around them.
                                </p>

                                <h2>🧩 What’s in the Worksheet?</h2>
                                <ul>
                                    <li>Picture-based comparison of big and small objects</li>
                                    <li>Circle the <strong>bigger/smaller</strong> item</li>
                                    <li>Match big objects with small versions</li>
                                    <li>Coloring or tracing based on size</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Recognize size differences: big vs. small</li>
                                    <li>Compare two or more objects visually</li>
                                    <li>Improve classification and early observation skills</li>
                                    <li>Strengthen vocabulary using descriptive size terms</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Show your child the pictures and ask: <strong>“Which is big? Which is small?”</strong></li>
                                    <li>Let them circle, color, or match the correct items.</li>
                                    <li>Reinforce learning with real-life examples: <strong>big ball vs. small ball, big spoon vs. small spoon</strong>.</li>
                                    <li>Encourage conversation using the words <strong>“big”</strong> and <strong>“small”</strong> in daily routines.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/long-short"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Long - Short</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/heavy-light"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Heavy - Light</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/full-empty"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Full - Empty</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Activity Is Important</h2>
                                <p>
                                    Understanding size is one of the <strong>first comparison skills</strong> children learn. It prepares them for math concepts like measurement, ordering, and even counting. It also improves their ability to observe and describe the world around them.
                                </p>
                            </article>
                            <h2>Which one is {questionType}?</h2>

                            <div style={{ display: "flex", justifyContent: "center", gap: "50px", margin: "20px 0" }}>
                                <div
                                    onClick={() => checkAnswer("left")}
                                    style={{ cursor: "pointer", fontSize: bigOnLeft ? "80px" : "40px" }}
                                >
                                    {emoji}
                                </div>
                                <div
                                    onClick={() => checkAnswer("right")}
                                    style={{ cursor: "pointer", fontSize: bigOnLeft ? "40px" : "80px" }}
                                >
                                    {emoji}
                                </div>
                            </div>

                            <p style={{ fontSize: "20px" }}>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default BigOrSmallGame;
